import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import store from './store';
import reportWebVitals from './reportWebVitals';
import 'bootstrap/dist/css/bootstrap.min.css';
import './bootstrap-overrides.css';
import './index.css';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider
} from 'react-router-dom';
import { Provider } from 'react-redux';

import UserHomeScreen from './screens/UserHomeScreen';
import BenchmarksListScreen from './screens/BenchmarksListScreen';
import BenchmarkDetailsScreen from './screens/BenchmarkDetailsScreen';
import DrivesListScreen from './screens/DrivesListScreen';
import DriveDetailsScreen from './screens/DriveDetailsScreen';
import UserSignInScreen from './screens/UserSignInScreen';
import SignUpScreen from './screens/SignUpScreen';
import VerifyEmailNoticeScreen from './screens/VerifyEmailNoticeScreen';
import VerifyEmailScreen from './screens/VerifyEmailScreen';
import UserForgotPasswordScreen from './screens/UserForgotPasswordScreen';
import UserResetPasswordScreen from './screens/UserResetPasswordScreen';
import DownloadScreen from './screens/DownloadScreen';

import PrivateRoute from './components/PrivateRoute';
import LegacyUserRedirect from './components/LegacyUserRedirect';
import MyProfileScreen from './screens/MyProfileScreen';
import MySystemsScreen from './screens/MySystemsScreen';

import AdminRoute from './components/AdminRoute';
import ChiefAdminRoute from './components/ChiefAdminRoute';
import ReadAllUsersDeleteUserScreen from './screens/admin/ReadAllUsersDeleteUserScreen';
import CreateUserByAdminScreen from './screens/admin/CreateUserByAdminScreen';
import ReadUpdateUserScreen from './screens/admin/ReadUpdateUserScreen';
import AdminHomeScreen from './screens/admin/AdminHomeScreen';
import AdminActionLogScreen from './screens/admin/AdminActionLogScreen';
import ContributorsScreen from './screens/admin/ContributorsScreen';
import AdminRoleManagementScreen from './screens/admin/AdminRoleManagementScreen';
import SubmissionStatsScreen from './screens/admin/SubmissionStatsScreen';

import BlogListScreen from './screens/BlogListScreen';
import BlogPostScreen from './screens/BlogPostScreen';
import MyBlogsScreen from './screens/MyBlogsScreen';
import BlogEditorScreen from './screens/BlogEditorScreen';
import AdminBlogsScreen from './screens/admin/AdminBlogsScreen';


const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<App />}>
      <Route index={true} path="/" element={<UserHomeScreen/>} />
      
      <Route path="/all_benchmarks/page/:pageNumber" element={<BenchmarksListScreen/>} />
      <Route path="/all_benchmarks" element={<BenchmarksListScreen/>} />
      <Route path="/all_benchmarks/benchmark/:benchmarkId" element={<BenchmarkDetailsScreen/>} />
      <Route path="/all_drives/page/:pageNumber" element={<DrivesListScreen/>} />
      <Route path="/all_drives" element={<DrivesListScreen/>} />
      <Route path="/all_drives/drive/:driveId" element={<DriveDetailsScreen/>} />
      <Route path="/download_jdiskmark_desktop_app" element={<DownloadScreen />} />
      <Route path="/sign_in" element={<UserSignInScreen />} />
      <Route path="/sign_up" element={<SignUpScreen />} />
      <Route path="/verify_e-mail_notice" element={<VerifyEmailNoticeScreen />} />
      <Route path="/verify_e-mail/:verifyToken" element={<VerifyEmailScreen />} />
      <Route path="/forgot_password" element={<UserForgotPasswordScreen />} />
      <Route path="/reset_password/:token" element={<UserResetPasswordScreen />} />

      <Route path="/blog/page/:pageNumber" element={<BlogListScreen/>} />
      <Route path="/blog" element={<BlogListScreen/>} />
      <Route path="/blog/post/:slug" element={<BlogPostScreen/>} />

      {/* Signed-in pages. No user id in the URL: the auth cookie identifies the user. */}
      <Route element={<PrivateRoute />}>
        <Route path="/me" element={<Navigate to="/me/profile" replace />} />
        <Route path="/me/profile" element={<MyProfileScreen />} />
        <Route path="/me/benchmarks/page/:pageNumber" element={<BenchmarksListScreen key="mine" scope="mine"/>} />
        <Route path="/me/benchmarks" element={<BenchmarksListScreen key="mine" scope="mine"/>} />
        <Route path="/me/benchmarks/benchmark/:benchmarkId" element={<BenchmarkDetailsScreen key="mine" scope="mine"/>} />
        <Route path="/me/drives/page/:pageNumber" element={<DrivesListScreen key="mine" scope="mine"/>} />
        <Route path="/me/drives" element={<DrivesListScreen key="mine" scope="mine"/>} />
        <Route path="/me/drives/drive/:driveId" element={<DriveDetailsScreen key="mine" scope="mine"/>} />
        <Route path="/me/systems" element={<MySystemsScreen/>} />
        <Route path="/me/blogs/page/:pageNumber" element={<MyBlogsScreen/>} />
        <Route path="/me/blogs" element={<MyBlogsScreen/>} />
        <Route path="/me/blogs/new" element={<BlogEditorScreen/>} />
        <Route path="/me/blogs/edit/:id" element={<BlogEditorScreen/>} />
      </Route>

      {/* Admin pages. :userId here is the user being managed, not the admin. */}
      <Route element={<AdminRoute />}>
        <Route path="/admin/home" element={<AdminHomeScreen />} />
        <Route path="/admin/all_users/add_user" element={<CreateUserByAdminScreen />} />
        <Route path="/admin/all_users/page/:pageNumber" element={<ReadAllUsersDeleteUserScreen />} />
        <Route path="/admin/all_users" element={<ReadAllUsersDeleteUserScreen />} />
        <Route path="/admin/all_users/user/:userId/edit_user" element={<ReadUpdateUserScreen />} />
        <Route path="/admin/all_users/user/:userId/delete_user" element={<ReadAllUsersDeleteUserScreen />} />
        <Route path="/admin/all_users/user/:userId" element={<ReadUpdateUserScreen />} />
        <Route path="/admin/action_log/page/:pageNumber" element={<AdminActionLogScreen />} />
        <Route path="/admin/action_log" element={<AdminActionLogScreen />} />
        <Route path="/admin/contributors" element={<ContributorsScreen />} />
        <Route path="/admin/submission_stats" element={<SubmissionStatsScreen />} />
        <Route path="/admin/blogs/page/:pageNumber" element={<AdminBlogsScreen/>} />
        <Route path="/admin/blogs" element={<AdminBlogsScreen/>} />
      </Route>

      <Route element={<ChiefAdminRoute />}>
        <Route path="/admin/role_management" element={<AdminRoleManagementScreen />} />
      </Route>

      {/* Old /user/<id>/... URLs (bookmarks, e-mails) → current paths */}
      <Route path="/user/:legacyId/*" element={<LegacyUserRedirect />} />
    </Route>
  )
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <RouterProvider router={router} />
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();